import { ChangeEvent, useEffect, useRef, useState } from "react";
import { USER } from "@types/user";
import LoadingIcon from "@common/LoadingIcon";
import { error_message } from "@constants/Errors";
import messages from "@constants/Messages";
import WarningModal from "@components/Modals/WarningModal";

const SESSION_CACHE_KEY = "userFileCache";

type CachedFile = {
  base64: string;
  name: string;
  type: string;
};

type UserImageTableProps = {
  selectedUsers: number[];
  setSelectedUsers: (data: number[]) => void;
  userInformation: USER[];
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const readCache = (): Record<number, CachedFile[]> =>
  JSON.parse(sessionStorage.getItem(SESSION_CACHE_KEY) || "{}");

const writeCache = (data: Record<number, CachedFile[]>) =>
  sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(data));

export const getFilesFromCache = (): Record<number, File[]> => {
  const cached = readCache();
  return Object.fromEntries(
    Object.entries(cached).map(([userId, files]) => [
      userId,
      files.map(({ base64, name, type }) => {
        const byteString = atob(base64.split(",")[1]);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        return new File([ab], name, { type });
      }),
    ]),
  );
};

export const clearFileCache = () =>
  sessionStorage.removeItem(SESSION_CACHE_KEY);

const UserImageTable = ({
  selectedUsers,
  setSelectedUsers,
  userInformation,
}: UserImageTableProps) => {
  const [userData, setUserData] = useState<USER[]>([]);
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [previewsMap, setPreviewsMap] = useState<Record<number, string[]>>({});
  const [warningModalOpen, setWarningModalOpen] = useState<boolean>(false);

  const fileInputs = useRef<Record<number, HTMLInputElement | null>>({});

  useEffect(() => {
    setUserData(userInformation);
  }, [userInformation]);

  useEffect(() => {
    const cached = readCache();
    if (Object.keys(cached).length > 0) {
      const previews: Record<number, string[]> = Object.fromEntries(
        Object.entries(cached).map(([userId, files]) => [
          userId,
          files.map((f) => f.base64),
        ]),
      );
      setPreviewsMap(previews);
    }
  }, []);

  useEffect(() => {
    const handleDragOver = (e: DragEvent) => e.preventDefault();
    const handleDrop = (e: DragEvent) => e.preventDefault();

    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("drop", handleDrop);

    return () => {
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("drop", handleDrop);
    };
  }, []);

  const handleUserSelect = (userId: number, force = false) => {
    setSelectedUsers((prevSelected) => {
      if (force && !prevSelected.includes(userId)) {
        return [...prevSelected, userId];
      } else if (!force && prevSelected.includes(userId)) {
        return prevSelected.filter((id) => id !== userId);
      } else if (!force) {
        return [...prevSelected, userId];
      }
      return prevSelected;
    });
  };

  const handleFileChange = async (
    userId: number,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    const base64Files = await Promise.all(
      selectedFiles.map(async (file) => ({
        base64: await fileToBase64(file),
        name: file.name,
        type: file.type,
      })),
    );

    const existing = readCache();
    writeCache({ ...existing, [userId]: base64Files });

    setPreviewsMap((prev) => ({
      ...prev,
      [userId]: base64Files.map((f) => f.base64),
    }));

    handleUserSelect(userId, true);
  };

  const handleDrop = (e: React.DragEvent, user: USER) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile && /\.(png|jpe?g)$/i.test(droppedFile.name)) {
      const syntheticEvent = {
        target: { files: [droppedFile] },
      } as unknown as ChangeEvent<HTMLInputElement>;
      handleFileChange(user.id, syntheticEvent);
    } else {
      setWarningModalOpen(true);
    }
  };

  const renderUserImage = (user: USER) => {
    const previewUrl = previewsMap[user.id]?.[0];
    const existingUrl = user.profile_image_url;
    const displayName = `${user.last_name} ${user.first_name}`;

    if (previewUrl || existingUrl) {
      return (
        <div className="flex flex-col justify-center items-center">
          <img
            src={previewUrl ?? existingUrl}
            onLoad={() => setImageLoading(false)}
            className="ml-10 w-24 h-24 rounded-full object-cover"
            alt={displayName}
          />
          <div className="pl-10 text-center text-sm text-gray-500 whitespace-nowrap">
            {displayName}
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col justify-center items-center">
        <svg
          className="ml-10 w-24 h-24"
          viewBox="0 0 100 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="100" height="100" rx="50" fill="#D9D9D9" />
          <path
            d="M50 50.375C42.373 50.375 36.25 44.252 36.25 36.625C36.25 29.1055 42.373 22.875 50 22.875C57.5195 22.875 63.75 29.1055 63.75 36.625C63.75 44.252 57.5195 50.375 50 50.375ZM55.3711 55.5312C65.6836 55.5312 74.0625 63.9102 74.0625 74.2227C74.0625 76.2637 72.3438 77.875 70.3027 77.875H29.5898C27.5488 77.875 25.9375 76.2637 25.9375 74.2227C25.9375 63.9102 34.209 55.5312 44.5215 55.5312H55.3711Z"
            fill="#8E8E8E"
          />
        </svg>
        <div className="pl-10 text-center text-sm text-gray-500 whitespace-nowrap">
          {displayName}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="border border-text-field-border bg-white rounded-2xl overflow-hidden">
        <div className="overflow-x-auto overflow-y-hidden mb-2">
          <table className="min-w-[950px] w-full table-fixed mayo-card-header rounded-2xl">
            <thead className="border-b border-text-field-border">
              <tr>
                <th className="py-4 text-left label-light-blue-xl min-w-[50px] w-15" />
                <th className="py-4 text-left label-light-blue-xl min-w-[50px] w-10">
                  氏名
                </th>
                <th className="py-4 text-left label-light-blue-xl min-w-[50px] w-10">
                  ユーザーID
                </th>
              </tr>
            </thead>
            <tbody className="mayo-card-body rounded-2xl">
              {userData.length === 0 ? (
                <tr>
                  <td />
                  <td colSpan={5} className="text-center py-4">
                    {error_message.user.no_data}
                  </td>
                </tr>
              ) : (
                userData.map((user) => (
                  <tr key={user.id}>
                    <td className="py-5 flex items-center gap-2.5 min-w-[20px]">
                      <div className="relative w-24 h-24">
                        {user.profile_image_url && imageLoading ? (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <LoadingIcon
                              width={8}
                              height={8}
                              textSize="text-sm"
                            />
                          </div>
                        ) : (
                          <div className="absolute inset-0 flex flex-row items-center justify-between px-2">
                            <div
                              className="absolute p-10 inset-10 flex items-center justify-center bg-gray-200 rounded-full"
                              onClick={() =>
                                fileInputs.current[user.id]?.click()
                              }
                            />
                            <div>
                              <div
                                className="ml-40 w-40 md:w-40 h-20 mt-2 border-2 border-dashed rounded-xl flex items-center justify-center cursor-pointer hover:border-blue-500 text-sm text-gray-500 hover:text-blue-500 transition-colors"
                                onClick={() =>
                                  fileInputs.current[user.id]?.click()
                                }
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                onDrop={(e) => handleDrop(e, user)}
                              >
                                <p className="text-center whitespace-pre">
                                  {messages.image.drag_and_drop}
                                </p>
                                <input
                                  type="file"
                                  accept=".png,.jpg,.jpeg"
                                  onChange={(e) => handleFileChange(user.id, e)}
                                  ref={(el) =>
                                    (fileInputs.current[user.id] = el)
                                  }
                                  style={{ display: "none" }}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        {renderUserImage(user)}
                      </div>
                    </td>
                    <td className="py-4 text-xl text-gray-900 min-w-[10px]">
                      {user.last_name} {user.first_name}
                    </td>
                    <td className="py-4 text-xl text-gray-900 min-w-[10px]">
                      {user.login_id}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <WarningModal
          title={messages.image.invalid_file_type_title}
          body={messages.image.invalid_file_type}
          confirmText=""
          cancelText="閉じる"
          isOpen={warningModalOpen}
          onRequestClose={() => setWarningModalOpen(false)}
        />
      </div>
    </div>
  );
};

export default UserImageTable;
